<template>
  <PageHeader
    :title="
      hasMultipleDashboards(userStore.roles) ? $t('ui.entities.user.role.callAgent') : $t('ui.entities.dashboard')
    "
  >
    <template #right>
      <div class="flex flex-col sm:flex-row gap-2">
        <div
          v-if="$can('manage_call_center_team')"
          class="flex flex-col sm:flex-row gap-2"
        >
          <Select
            v-model="callCenterSelected"
            :options="callCentersAllowed"
            icon="headset"
            value-key="id"
            label-key="name"
            :placeholder="`Select a Call Center`"
            class="min-w-56"
            :readonly="callCentersAllowed?.length == 1"
            @select="updateCallCenterSelected"
          ></Select>
          <Select
            v-model="agentSelected"
            v-tippy="!callCenterSelected ? 'Select a call center' : ''"
            mode="single"
            :close-on-select="true"
            :searchable="true"
            :options="callAgents"
            label-key="fullName"
            value-key="id"
            class="min-w-56"
            :icon="agentSelected ? null : 'user'"
            placeholder="Select an Agent"
            :disabled="!callCenterSelected"
            @select="onAgentChange()"
          >
            <template #value="{ value }">
              <div class="flex items-center">
                <Avatar
                  :url="value.profilePicUrl"
                  :text="value.initials"
                  size="xs"
                  class="mr-2"
                />
                {{ value.fullName }}
              </div>
            </template>

            <template #option="{ option }">
              <div class="flex items-center">
                <Avatar
                  :url="option.profilePicUrl"
                  :text="option.initials"
                  size="xs"
                  class="mr-2"
                />
                {{ option.fullName }}
              </div>
            </template>
          </Select>
        </div>
        <div>
          <DatePicker
            v-model="selectedDate"
            class="w-full"
            selection-mode="single"
            :months-visible="1"
            :min-date="monthStart"
            :max-date="monthEnd"
            close-on-select
            @select="onDateChange"
          />
        </div>
      </div>
    </template>
  </PageHeader>

  <div v-if="callCentersAllowed?.length">
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
      <div class="flex flex-col">
        <!-- Shifts Card -->
        <Card
          :is-loading="dataApiRequest.isLoading.value"
          class="row-span-2"
        >
          <CardBody>
            <CardTitle title="Today's Shifts" />
            <div v-if="dataIsLoading">
              <Skeleton
                height="4rem"
                width="100%"
                class="mb-2"
              />
              <Skeleton
                height="4rem"
                width="100%"
                class="mb-2"
              />
            </div>
            <div v-else>
              <div v-if="filteredShifts.length > 0">
                <ShiftSummary
                  v-for="shift in filteredShifts"
                  :key="shift.id"
                  :shift-data="shift"
                  :shift-types="shiftTypes"
                  class="mb-2"
                />
              </div>
              <div
                v-else
                class="text-secondary"
              >
                No Shifts
              </div>
            </div>
          </CardBody>
        </Card>

        <!-- Tasks Card -->
        <Card
          :is-loading="taskStore.isLoading || dataApiRequest.isLoading.value"
          class="row-span-2"
        >
          <CardBody>
            <div class="flex justify-between">
              <CardTitle title="Tasks" />

              <Button
                severity="primary"
                text
                label="View Tasks"
                size="xs"
                router-link="/tasks"
              />
            </div>

            <div class="flex flex-col gap-2">
              <div class="flex gap-2 items-center justify-between flex-row-reverse">
                <Chip severity="danger">{{ taskCounts.overdue }}</Chip>
                Overdue
              </div>
              <div class="flex gap-2 items-center justify-between flex-row-reverse">
                <Chip severity="warning">{{ taskCounts.dueToday }}</Chip>
                Due Today
              </div>
              <div class="flex gap-2 items-center justify-between flex-row-reverse">
                <Chip severity="brand">{{ taskCounts.dueTomorrow }}</Chip>
                Due Tomorrow
              </div>
            </div>
          </CardBody>
        </Card>
        <!-- <div class="employee-of-month">
        <Card no-margin>
          <CardBody class="my-[-2px]">
            <div class="flex gap-6 items-center">
              <div class="w-12 h-12 relative">
                <img v-if="profilePicUrl != null && !userImgHasError" :src="profilePicUrl" class="rounded-full" @error="imgError(url)" />
                <div
                  class="absolute -right-1 -top-2 w-6 h-6 bg-darkmode-3 flex justify-center items-center rounded-full border-2 border-white dark:border-darkmode-1 rotate-12"
                >
                  <Icon type="crown" class="text-sm" style="color: #ffc531" />
                </div>
              </div>

              <div>
                <div class="text-secondary text-sm leading-[16px]">Employee of the month</div>
                <div class="font-medium text-lg leading-[16px]">Anna Magnusson</div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div> -->
      </div>

      <!-- Stats Card -->
      <div class="col-span-2">
        <Card :is-loading="dataApiRequest.isLoading.value">
          <CardBody>
            <div v-if="dataIsLoading">
              <div class="flex justify-between mr-[100px]">
                <div class="w-[25%]">&nbsp;</div>
                <Skeleton
                  height="2rem"
                  width="50px"
                  class="mb-2"
                />
                <Skeleton
                  height="2rem"
                  width="50px"
                  class="mb-2"
                />
                <Skeleton
                  height="2rem"
                  width="50px"
                  class="mb-2"
                />
              </div>
              <Skeleton
                height="3rem"
                width="100%"
                class="mb-2"
              />
              <Skeleton
                height="3rem"
                width="100%"
                class="mb-2"
              />
              <Skeleton
                height="3rem"
                width="100%"
                class="mb-2"
              />
            </div>
            <div v-else>
              <StatsTable
                column-template="1fr 1fr 1fr 1fr"
                :headers="[
                  { type: 'icon', iconValue: 'user-clock', label: 'Productive Hours' },
                  { type: 'icon', iconValue: 'phone', label: 'Call Target' },
                  { type: 'icon', iconValue: 'calendar', label: 'Appointment Target' }
                ]"
                :rows-data="[
                  [
                    { isLabel: true, value: dateLabels.date },
                    {
                      field: 'productiveHours',
                      value: filteredStats.today.productiveHours
                    },
                    {
                      field: 'calls',
                      value: filteredStats.today.callsAttempted,
                      target: filteredStats.today.callTarget
                    },
                    {
                      field: 'appointments',
                      value: filteredStats.today.appointmentsMade,
                      target: filteredStats.today.appointmentTarget
                    }
                  ],
                  [
                    { isLabel: true, value: dateLabels.week },
                    {
                      field: 'productiveHours',
                      value: filteredStats.week.productiveHours
                    },
                    {
                      field: 'calls',
                      value: filteredStats.week.callsAttempted,
                      target: filteredStats.week.callTarget
                    },
                    {
                      field: 'appointments',
                      value: filteredStats.week.appointmentsMade,
                      target: filteredStats.week.appointmentTarget
                    }
                  ],
                  [
                    { isLabel: true, value: dateLabels.month },
                    {
                      field: 'productiveHours',
                      value: filteredStats.month.productiveHours
                    },
                    {
                      field: 'calls',
                      value: filteredStats.month.callsAttempted,
                      target: filteredStats.month.callTarget
                    },
                    {
                      field: 'appointments',
                      value: filteredStats.month.appointmentsMade,
                      target: filteredStats.month.appointmentTarget
                    }
                  ]
                ]"
              >
                <template #productiveHours="{ data }">
                  {{ data.value }}
                </template>
                <template #calls="{ data }">
                  {{ data.value }}
                  <span class="text-quaternary">/ {{ data.target }}</span>
                </template>
                <template #appointments="{ data }">
                  {{ data.value }}
                  <span class="text-quaternary">/ {{ data.target }}</span>
                </template>
              </StatsTable>
            </div>
          </CardBody>
        </Card>
      </div>

      <!-- KPI Card -->
      <div class="col-span-1">
        <Card
          :is-loading="dataApiRequest.isLoading.value"
          class="row-span-2"
        >
          <div class="flex flex-col gap-4">
            <!-- KPI Rings -->
            <CardBody>
              <div class="flex justify-between">
                <CardTitle title="KPI" />
                <div>
                  <!-- <div class="text-[0.9rem] flex justify-between border border-gray-300 dark:border-darkmode-3 rounded-md">
                      <Dropdown>
                        <template #triggerContent>
                          <div class="cursor-pointer py-1 px-2 flex items-center justify-center">
                            <div>
                              {{ kpiRingDateRange }}
                            </div>
                            <Icon type="chevron-down" class="ml-1" />
                          </div>
                        </template>
                        <div v-for="option in kpiRingOptions">
                          <DropdownItem @item-clicked="kpiRingUpdateDateRange(option)">
                            <div class="flex items-center">{{ option }}</div>
                          </DropdownItem>
                        </div>
                      </Dropdown>
                    </div> -->
                  {{ dateLabels.date }}
                </div>
              </div>

              <KpiRings
                v-model="computedData.kpiRings"
                class="max-h-[200px]"
                :hover-stats="true"
                size="lg"
              />
            </CardBody>

            <!-- KPI Scores Grid -->
            <CardBody class="!pt-0">
              <!-- <CardTitle title="Performance" /> -->
              <div class="flex flex-col gap-8">
                <KpiScoresGrid
                  v-model="computedData.kpiScoresGrid"
                  view="month"
                />
              </div>
            </CardBody>

            <Separator />

            <!-- Streak Card -->
            <CardBody class="!pt-0">
              <div class="flex justify-around">
                <ScoreBadge
                  v-model="computedData.badges.streak"
                  badge="streak"
                />
                <!-- <ScoreBadge v-model="computedData.badges.achievements" badge="achievements" />
    <ScoreBadge v-model="computedData.badges.leaderboard" badge="leaderboard" /> -->
              </div>
            </CardBody>
          </div>
        </Card>
      </div>
    </div>
  </div>
  <Empty
    v-else-if="!callCentersAllowed.length"
    title="No call centers"
    description="You haven't been added to any call centers"
    icon="headset"
  />
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue'

// components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import StatsTable from '@/components/dashboard/stats/StatsTable.vue'
import Statistic from '@/components/dashboard/stats/Statistic.vue'
import ScoreBadge from '@/components/gamification/ScoreBadge.vue'
import ShiftSummary from '@/components/dashboard/ShiftSummary.vue'
import KpiRings from '@/components/dashboard/KpiRings.vue'
import Button from '@/components/button/Button.vue'
import KpiScoresGrid from '@/components/dashboard/widgets/KpiScoresGrid.vue'
import Select from '@/components/forms/Select.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import Separator from '@/components/separator/Separator.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import Chip from '@/components/chip/Chip.vue'

// utilities
import { alertToast } from '@/utilities/notification'
import { hasMultipleDashboards } from '@/utilities/dashboards'

// stores
import { useUserStore } from '@/stores/UserStore.ts'
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useTaskStore } from '@/stores/TaskStore.ts'

// composables
import useApiRequest from '@/composables/useApiRequest'

import { useAbility } from '@casl/vue'
import dayjs from 'dayjs'

const userStore = useUserStore()
const authStore = useAuthStore()
const taskStore = useTaskStore()
const { can } = useAbility()

const dashboardData = ref()
const callCentersAllowed = userStore.callCenters

// Users List
const callAgents = ref()
const dataApiRequest = useApiRequest()
const callAgentsRequest = useApiRequest()
const shiftTypesApiRequest = useApiRequest()

/**
 * Computed property to check if dashboard data is loading
 */
const dataIsLoading = computed(() => dataApiRequest.isLoading.value)

const agentSelected = ref(null)
const callCenterSelected = ref(null)
const shiftTypes = ref([])

// Selected date for filtering dashboard data
const selectedDate = ref(dayjs().startOf('day').format('YYYY-MM-DD'))

/**
 * Computed properties for current month date restrictions
 */
const monthStart = computed(() => dayjs().startOf('month').format('YYYY-MM-DD'))
const monthEnd = computed(() => dayjs().endOf('month').format('YYYY-MM-DD'))

if (!localStorage.getItem('report-filter-call-center-selected')) {
  localStorage.setItem('report-filter-call-center-selected', callCentersAllowed[0]?.id)
  callCenterSelected.value = callCentersAllowed[0]?.id
} else {
  callCenterSelected.value = parseInt(localStorage.getItem('report-filter-call-center-selected'))
}

if (callCenterSelected.value) {
  fetchCallAgents()
}

function fetchShiftTypes() {
  try {
    shiftTypesApiRequest
      .send({
        method: 'GET',
        endpoint: '/v1/users/schedule/shift-types'
      })
      .then(response => {
        if (response.success) {
          shiftTypes.value = response.data
        } else {
          alertToast('Failed to fetch shift types', response.message, 'error')
        }
      })
  } catch (error) {
    alertToast('Failed to fetch shift types', error.message, 'error')
  }
}

// Fetch shift types on component mount
fetchShiftTypes()

/**
 * Fetches dashboard data for the selected agent (entire month's data)
 * If no agent is selected, uses the logged-in user's ID
 */
function fetchDashboardData() {
  // Use selected agent or fall back to logged-in user
  const userId = agentSelected.value || authStore.details.id

  if (!userId) {
    return
  }

  dataApiRequest
    .send({
      endpoint: '/v1/dashboards/call-agent',
      params: {
        userId: userId
      }
    })
    .then(response => {
      dashboardData.value = response.data
    })
}

/**
 * Initialize default agent selection and fetch data
 */
function initializeDashboard() {
  // Set default agent to logged-in user if not a manager or if manager hasn't selected anyone
  if (!can('manage_call_center_team') && userStore.roles.includes(2)) {
    // Non-managers: always use their own ID
    agentSelected.value = userStore.details.id
  } else if (!agentSelected.value && authStore.details.id) {
    // Managers: default to logged-in user if no selection made
    agentSelected.value = authStore.details.id
  }

  // Fetch data if we have a user (either selected or default)
  if (agentSelected.value || authStore.details.id) {
    fetchDashboardData()
  }

  // Fetch tasks for the current user
  taskStore.fetchTasks()
}

// Initialize dashboard when component is mounted
onMounted(() => {
  initializeDashboard()
})

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
  fetchCallAgents()
}

function fetchCallAgents() {
  try {
    callAgentsRequest
      .send({
        method: 'GET',
        endpoint: '/v1/users',
        params: {
          userType: 'all',
          callCenterId: callCenterSelected.value
        }
      })
      .then(response => {
        if (response.success) {
          if (response.data.length == 0) {
            callAgents.value = []
          } else {
            callAgents.value = response.data
            // Order callAgents by name
            callAgents.value?.sort((a, b) => {
              return a.fullName.localeCompare(b.fullName)
            })
          }
        } else {
          alertToast('Failed to fetch users', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch users', err.message, 'error')
  }
}

/**
 * Handles agent selection change and fetches dashboard data
 */
function onAgentChange() {
  nextTick(() => {
    if (callCenterSelected.value) {
      // Fetch data for selected agent, or logged-in user if none selected
      fetchDashboardData()
      // Refresh tasks when agent changes (tasks are always for logged-in user)
      taskStore.fetchTasks()
    } else {
      // Empty the dashboard data if no call center selected
      dashboardData.value = null
    }
  })
}

/**
 * Handles date change - no need to fetch data as it's already loaded
 * @param {string|string[]} dateValue - The selected date value
 */
function onDateChange(dateValue) {
  // Handle case where dateValue might be an array
  let normalizedDate = dateValue
  if (Array.isArray(dateValue)) {
    normalizedDate = dayjs(dateValue[0]).format('YYYY-MM-DD')
  } else if (dateValue) {
    normalizedDate = dayjs(dateValue).format('YYYY-MM-DD')
  }

  // Update the ref if the normalized date is different
  if (selectedDate.value !== normalizedDate) {
    selectedDate.value = normalizedDate
  }
}

// Watch for date changes (handles v-model updates and normalization)
watch(selectedDate, newDate => {
  // Normalize the date value in case it's an array or different format
  if (Array.isArray(newDate)) {
    const normalizedDate = dayjs(newDate[0]).format('YYYY-MM-DD')
    // Update the ref to normalized value (this won't trigger another watch cycle since value changes)
    if (selectedDate.value !== normalizedDate) {
      selectedDate.value = normalizedDate
    }
    return
  }
  // No need to fetch data - it's already loaded for the entire month
})

/**
 * Computed property to filter shifts by selected date
 */
const filteredShifts = computed(() => {
  if (!dashboardData.value?.todaysShifts || !selectedDate.value) {
    return []
  }
  return dashboardData.value.todaysShifts.filter(shift => {
    const shiftDate = dayjs(shift.period || shift.date).format('YYYY-MM-DD')
    return shiftDate === selectedDate.value
  })
})

/**
 * Computed property for dynamic date labels
 */
const dateLabels = computed(() => {
  if (!selectedDate.value) {
    return {
      date: 'Today',
      week: 'This Week',
      month: 'This Month'
    }
  }

  const selectedDateObj = dayjs(selectedDate.value)
  const weekStart = selectedDateObj.startOf('isoWeek')
  const weekEnd = selectedDateObj.endOf('isoWeek')
  const isToday = selectedDateObj.isSame(dayjs(), 'day')
  const isCurrentWeek = weekStart.isSame(dayjs().startOf('isoWeek'), 'day')
  const isCurrentMonth = selectedDateObj.isSame(dayjs(), 'month')

  // Format date label
  let dateLabel = selectedDateObj.format('MMM D')
  if (isToday) {
    dateLabel = 'Today'
  }

  // Format week label
  let weekLabel = `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`
  if (isCurrentWeek) {
    weekLabel = 'This Week'
  } else {
    // Show range from week start to selected date if not full week
    if (!selectedDateObj.isSame(weekEnd, 'day')) {
      weekLabel = `${weekStart.format('MMM D')} - ${selectedDateObj.format('MMM D')}`
    }
  }

  // Format month label
  let monthLabel = selectedDateObj.format('MMMM')
  if (isCurrentMonth) {
    monthLabel = 'This Month'
  }

  return {
    date: dateLabel,
    week: weekLabel,
    month: monthLabel
  }
})

/**
 * Computed property to calculate filtered stats based on selected date
 */
const filteredStats = computed(() => {
  if (!dashboardData.value || !selectedDate.value) {
    return {
      today: { productiveHours: 0, callsAttempted: 0, appointmentsMade: 0, callTarget: 0, appointmentTarget: 0 },
      week: { productiveHours: 0, callsAttempted: 0, appointmentsMade: 0, callTarget: 0, appointmentTarget: 0 },
      month: { productiveHours: 0, callsAttempted: 0, appointmentsMade: 0, callTarget: 0, appointmentTarget: 0 }
    }
  }

  const selectedDateObj = dayjs(selectedDate.value)
  const weekStart = selectedDateObj.startOf('isoWeek')
  const monthStart = selectedDateObj.startOf('month')

  // Get stats directly from performance array - each day now has stats and targets objects
  const getStatsForDate = date => {
    const dateStr = dayjs(date).format('YYYY-MM-DD')
    const perf = dashboardData.value?.performance?.find(p => p.date === dateStr)

    if (!perf || !perf.stats) {
      return {
        callsInitiated: 0,
        callsAttempted: 0,
        appointmentsMade: 0,
        productiveHours: 0,
        callTarget: 0,
        appointmentTarget: 0
      }
    }

    return {
      callsInitiated: perf.stats.callsInitiated || 0,
      callsAttempted: perf.stats.callsAttempted || 0,
      appointmentsMade: perf.stats.appointmentsMade || 0,
      productiveHours: perf.stats.productiveHours || 0,
      callTarget: perf.targets?.callTarget || 0,
      appointmentTarget: perf.targets?.appointmentTarget || 0
    }
  }

  // Calculate stats for selected date
  const todayStats = getStatsForDate(selectedDate.value)

  // Calculate week stats (sum from week start to selected date)
  const weekStats = { callsAttempted: 0, appointmentsMade: 0, productiveHours: 0, callTarget: 0, appointmentTarget: 0 }
  let currentDate = weekStart.clone()
  while (currentDate.isBefore(selectedDateObj, 'day') || currentDate.isSame(selectedDateObj, 'day')) {
    const stats = getStatsForDate(currentDate)
    weekStats.callsAttempted += stats.callsAttempted
    weekStats.appointmentsMade += stats.appointmentsMade
    weekStats.productiveHours += stats.productiveHours
    weekStats.callTarget += stats.callTarget
    weekStats.appointmentTarget += stats.appointmentTarget
    currentDate = currentDate.add(1, 'day')
  }

  // Calculate month stats (sum from month start to selected date)
  const monthStats = { callsAttempted: 0, appointmentsMade: 0, productiveHours: 0, callTarget: 0, appointmentTarget: 0 }
  currentDate = monthStart.clone()
  while (currentDate.isBefore(selectedDateObj, 'day') || currentDate.isSame(selectedDateObj, 'day')) {
    const stats = getStatsForDate(currentDate)
    monthStats.callsAttempted += stats.callsAttempted
    monthStats.appointmentsMade += stats.appointmentsMade
    monthStats.productiveHours += stats.productiveHours
    monthStats.callTarget += stats.callTarget
    monthStats.appointmentTarget += stats.appointmentTarget
    currentDate = currentDate.add(1, 'day')
  }

  return {
    today: {
      productiveHours: todayStats.productiveHours,
      callsAttempted: todayStats.callsAttempted,
      appointmentsMade: todayStats.appointmentsMade,
      callTarget: todayStats.callTarget,
      appointmentTarget: todayStats.appointmentTarget
    },
    week: {
      productiveHours: weekStats.productiveHours,
      callsAttempted: weekStats.callsAttempted,
      appointmentsMade: weekStats.appointmentsMade,
      callTarget: weekStats.callTarget,
      appointmentTarget: weekStats.appointmentTarget
    },
    month: {
      productiveHours: monthStats.productiveHours,
      callsAttempted: monthStats.callsAttempted,
      appointmentsMade: monthStats.appointmentsMade,
      callTarget: monthStats.callTarget,
      appointmentTarget: monthStats.appointmentTarget
    }
  }
})

/**
 * Computed property to get calls comparison (initiated vs attempted) for selected date
 */
const callsComparison = computed(() => {
  if (!dashboardData.value || !selectedDate.value) {
    return {
      initiated: 0,
      attempted: 0
    }
  }

  const dateStr = dayjs(selectedDate.value).format('YYYY-MM-DD')
  const perf = dashboardData.value?.performance?.find(p => p.date === dateStr)

  if (!perf || !perf.stats) {
    return {
      initiated: 0,
      attempted: 0
    }
  }

  return {
    initiated: perf.stats.callsInitiated || 0,
    attempted: perf.stats.callsAttempted || 0
  }
})

/**
 * Computed property to calculate task counts from TaskStore
 * Returns counts for overdue, due today, and due tomorrow tasks
 */
const taskCounts = computed(() => {
  if (!taskStore.tasks || taskStore.tasks.length === 0) {
    return {
      overdue: 0,
      dueToday: 0,
      dueTomorrow: 0
    }
  }

  const now = dayjs()
  const today = now.startOf('day')
  const tomorrow = now.add(1, 'day').startOf('day')

  let overdue = 0
  let dueToday = 0
  let dueTomorrow = 0

  taskStore.tasks.forEach(task => {
    // Filter out completed and deleted tasks
    if (task.isCompleted || task.isDeleted) {
      return
    }

    // Must have a valid due date
    if (!task.dueDate || task.dueDate === '0000-00-00 00:00:00') {
      return
    }

    // Check if due date is valid
    const dueDate = dayjs(task.dueDate)
    if (!dueDate.isValid()) {
      return
    }

    const dueDateStart = dueDate.startOf('day')

    // Count overdue tasks (before today)
    if (dueDateStart.isBefore(today)) {
      overdue++
    }
    // Count tasks due today
    else if (dueDateStart.isSame(today)) {
      dueToday++
    }
    // Count tasks due tomorrow
    else if (dueDateStart.isSame(tomorrow)) {
      dueTomorrow++
    }
  })

  return {
    overdue,
    dueToday,
    dueTomorrow
  }
})

const computedData = computed(() => {
  let kpiRings = [{ value: 0 }, { value: 0 }, { value: 0 }, { value: 0 }, { value: 0 }]
  const selectedDatePerformance = dashboardData.value?.performance?.find(date => date.date === selectedDate.value)

  if (selectedDatePerformance) {
    kpiRings = [
      { value: selectedDatePerformance.kpi.productivity * 100 },
      { value: selectedDatePerformance.kpi.answerRate * 100 },
      { value: selectedDatePerformance.kpi.opportunityRate * 100 },
      { value: selectedDatePerformance.kpi.conversionRate * 100 },
      { value: selectedDatePerformance.kpi.appVsCallConversion * 100 }
      // { value: selectedDatePerformance.kpi.complianceRate * 100 }
    ]
  }

  let kpiScoresGrid = []
  let streak = 0

  if (dashboardData.value?.performance) {
    kpiScoresGrid = dashboardData.value.performance.map(date => {
      // Get productiveHours from stats to determine if it's a working day
      const productiveHours = date.stats?.productiveHours ?? null
      const isWorkingDay = productiveHours !== null && productiveHours > 0

      return {
        period: date.date,
        productivity: date.kpi.productivity,
        answerRate: date.kpi.answerRate,
        opportunityRate: date.kpi.opportunityRate,
        conversionRate: date.kpi.conversionRate,
        appVsCallConversion: date.kpi.appVsCallConversion,
        score: date.kpi.total !== null ? date.kpi.totalKpisHit : null,
        isWorkingDay: isWorkingDay,
        productiveHours: productiveHours
      }
    })

    /**
     * Calculate streak: Count consecutive working days (from selected date backwards)
     * where all KPIs are hit (score >= 1).
     *
     * Rules:
     * - A working day is defined as a day with productiveHours > 0
     * - Non-working days (productiveHours is null or 0) break the streak
     * - Working days where KPIs are not hit (score < 1 or null) break the streak
     * - Only consecutive working days with score >= 1 count towards the streak
     * - The streak counts backwards from the selected date
     */
    const selectedDateObj = dayjs(selectedDate.value)

    // Filter to selected date and earlier, then sort by date descending (most recent first)
    const daysToCheck = kpiScoresGrid
      .filter(day => {
        const dayDate = dayjs(day.period)
        return !dayDate.isAfter(selectedDateObj, 'day')
      })
      .sort((a, b) => {
        const dateA = dayjs(a.period)
        const dateB = dayjs(b.period)
        return dateB.diff(dateA, 'day') // Descending order (most recent first)
      })

    // Track the last processed working day date to ensure we're checking consecutive working days
    let lastWorkingDayDate = null

    // Loop backwards from selected date
    for (const day of daysToCheck) {
      const dayDate = dayjs(day.period)

      // Check if this is a consecutive working day
      // If we've already processed a working day, ensure this day is the next consecutive day
      if (lastWorkingDayDate !== null) {
        const daysDiff = lastWorkingDayDate.diff(dayDate, 'day')
        // If there's a gap of more than 1 day between working days, break the streak
        // This handles cases where there are non-working days between working days
        if (daysDiff > 1) {
          break
        }
      }

      // If it's not a working day (no productiveHours or productiveHours is 0), break the streak
      if (!day.isWorkingDay) {
        break
      }

      // If it's a working day but score is null (KPIs couldn't be calculated), break the streak
      if (day.score === null) {
        break
      }

      // If it's a working day and all KPIs are hit (score >= 1), increment streak
      if (day.score >= 1) {
        streak++
        lastWorkingDayDate = dayDate
      } else {
        // Working day but KPIs not hit, break the streak
        break
      }
    }
  }

  return {
    kpiRings: kpiRings,
    kpiScoresGrid: kpiScoresGrid,
    badges: {
      streak: streak,
      achievements: 2,
      leaderboard: 3
    }
  }
})
</script>

<style>
.employee-of-month {
  border-radius: 0.6rem;
  padding: 2px;
  background: linear-gradient(45deg, rgba(113, 83, 163, 1) 0%, rgba(94, 193, 224, 1) 49%, rgba(207, 69, 153, 1) 100%);
}
</style>
